import {
  ErrorResponse,
  JoinRoomRequest,
  JoinRoomResponse,
  ServerResponse,
  RoomActionRequest,
  WsMessage,
  WsResponse,
  MessageRequest,
  MessageResponse,
  RoomMessageRequest, PushNotificationSubscription, ConferenceAdmitUserRequest, ConferenceAdmitUserResponse,
} from "@inpriton/types";
import {
  ConferenceJoinRequest, ConferenceJoinResponse,
  ConferenceRemoveUserRequest, ConferenceRemoveUserResponse,
  ConferenceEndRequest, ConferenceEndResponse,
  SessionEndRequest, SessionEndResponse,
  TokenRefreshRequest, TokenRefreshResponse,
  TranslationConferenceTokenData,
} from "@inpriton/types/conferenceTypes";
import {v4} from 'uuid'
import {jwtDecode} from "jwt-decode"
import {API_URL} from "../config"

// RISKS.md/P1.20 + P1.22: why the session is dead — a failed refresh, or a refused re-join.
// 'revoked' = the server rejected it (kicked, session deleted, reuse-detection); 'rejected' = an
// unusable refresh response; 'invalid-token' = no usable token to refresh from; 'removed' = a ban.
// All four are terminal — unlike a network failure, which retries.
export type SessionDeadReason = 'revoked' | 'rejected' | 'invalid-token' | 'removed'

export const DISCONNECTED_CAUSE = 'disconnected'

// RISKS.md/P2.15: a send that lost its socket is NOT a failure to report — leaving, a kick and a
// network drop all reject every pending request this way, and the reconnect overlay and the exit
// notice already own those states. Worse, the rejection lands after App's unmount ran
// clearNotifications(), so a toast raised here would ambush the user on their next visit.
export const isDisconnectRejection = (e: unknown) =>
  e instanceof Error && e.cause === DISCONNECTED_CAUSE

export default class WsClient {
  private url: string;
  private protocols: string;
  // @ts-ignore
  private ws: WebSocket;
  private callback: ((data: WsMessage) => void)|null = null;

  private messageCallbacks: { [messageId: string]: {
      resolve: (value: any) => void
      reject: (reason?: any) => void
  } } = {}

  public token: string | null = null
  // RISKS.md/P1.19: opaque refresh token bound to this session, handed back by conference:join.
  // Kept IN MEMORY only (never localStorage/URL) — a reload re-bootstraps from the join URL token.
  private refreshToken: string | null = null
  private refreshTimer: ReturnType<typeof setTimeout> | null = null
  // RISKS.md/P1.20: single-flight guard — a forced refresh (a REST call that hit 401) and the
  // scheduled one must not both rotate, or the loser presents a rotated-out token and trips
  // reuse-detection. Concurrent callers await the same attempt.
  private refreshPromise: Promise<boolean> | null = null
  // RISKS.md/P1.20: notified when the session is definitively unusable (refresh rejected /
  // revoked / unusable token). Previously these branches just returned, so silent refresh died
  // quietly and the user sat in a meeting whose credentials were already dead.
  private sessionDeadHandlers: ((reason: SessionDeadReason) => void)[] = []
  // Admission logic: a FRESH (re)join via the green room builds a new WsClient, and the server
  // clears admission for it unless another tab of this browser is in the meeting (RISKS.md/P2.180);
  // a transient auto-reconnect reuses THIS one and keeps admission.
  // ⚠ RISKS.md/P1.22: gated on `joined`, so it means "a join already SUCCEEDED here" — a first
  // attempt whose socket never opened also closes, and counting that as a reconnect would skip the
  // first-join flow and claim an admission never granted. Never reset: one blip and it stays true.
  private reconnecting = false
  private joined = false
  private instanceId: string;
  private disconnected: boolean = false;
  // RISKS.md/P1.3: consecutive failed connection attempts — drives the exponential reconnect
  // backoff below; reset once a connection actually opens.
  private reconnectAttempts = 0
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null
  // RISKS.md/P1.3: set by a deliberate disconnect() (and lifted by connect()) — while set,
  // send() rejects instead of polling forever for a socket that will never reopen.
  private closed = false

  private connectionStateHandlers: ((connected: boolean) => void)[] = []

  constructor(url: string, protocols: string) {
    this.instanceId = v4()
    this.url = url;
    this.protocols = protocols;
  }

  public disconnect() {
    // RISKS.md/P1.3: terminal until the next connect() — queued send()s reject rather than wait,
    // and a pending auto-reconnect must not resurrect a deliberately closed session.
    this.closed = true
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
    this.clearRefreshTimer()
    if (this.ws) {
      this.disconnected = true
      this.ws.close()
    }
  }

  // RISKS.md/P1.22: closing IS the retry — onclose schedules the next attempt on the existing
  // backoff. For a socket that is open but unusable, which nothing else would recover.
  public forceReconnect() {
    this.ws?.close()
  }

  private clearRefreshTimer() {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer)
      this.refreshTimer = null
    }
  }

  public onStateChange(callback: (connected: boolean) => void) {
    this.connectionStateHandlers.push(callback)
  }

  public onMessage(callback: ((message: WsMessage) => void)|null) {
    console.trace('WSCLIENT', this.instanceId, 'registered callback')
    this.callback = callback;
  }

  public sendUserMessage(user: string, room: string, message: ArrayBuffer, self: ArrayBuffer) {
    return this.send<MessageRequest, MessageResponse>({
      action: 'message',
      kind: 'user',
      room,
      user,
      message,
      self,
    })
  }

  public sendRoomMessage(room: string, messages: { [uuid: string]: string }) {
    return this.send<RoomMessageRequest, MessageResponse>({
      action: 'room:message',
      kind: 'room',
      room,
      messages,
    })
  }

  public sendRoomAction(room: string, kind: 'typing' | 'talking', value: boolean) {
    // RISKS.md/P1.3: the server's room:action handler never replies — fire-and-forget.
    return this.send<RoomActionRequest>({
      action: 'room:action',
      room,
      kind,
      value,
    }, false)
  }

  public joinRoom(room: string): Promise<JoinRoomResponse> {
    if (!this.token) {
      throw new Error('not logged in')
    }

    return this.send<JoinRoomRequest, JoinRoomResponse>({
      action: 'room:join',
      room,
    })
  }

  async send<T extends WsMessage, T2 extends WsResponse | null = null>(data: T, expectReply = true): Promise<T2> {
    if (!this.ws || this.ws.readyState !== this.ws.OPEN) {
      // Queue until the socket (re)opens — a transient auto-reconnect resumes waiting requests.
      await new Promise((resolve2, reject2) => {
        const check = () => {
          // RISKS.md/P1.3: after a deliberate disconnect() nothing will reopen the socket —
          // reject instead of polling forever.
          if (this.closed) {
            reject2(new Error('websocket disconnected', { cause: DISCONNECTED_CAUSE }))
            return
          }
          if (!this.ws || this.ws.readyState !== this.ws.OPEN) {
            setTimeout(check, 500)
            return
          }
          resolve2(null)
        }
        check()
      })
    }
    const messageId = v4()

    const request = {
      messageId,
      ...(data as object),
    }

    console.debug('ws-client', '>', request)
    this.ws.send(JSON.stringify(request))

    // RISKS.md/P1.3: fire-and-forget actions (pong, room:action, disableWebRtcTrack) get no
    // server reply — registering a callback would leave a permanently-pending entry that turns
    // into a spurious rejection at every disconnect (one per elapsed ping in a long meeting).
    if (!expectReply) {
      return null as T2
    }

    return new Promise((resolve, reject) => {
      this.messageCallbacks[messageId] = { resolve, reject }
    })
  }

  public async joinConference(name: string, publicKey: string, pushNotifications: PushNotificationSubscription|null): Promise<ConferenceJoinResponse> {
    const response = await this.send<ConferenceJoinRequest, ConferenceJoinResponse>({
      action: 'conference:join',
      token: this.token as string,
      name,
      publicKey,
      pushNotifications,
      // false on a fresh green-room join, true on a transient auto-reconnect (-> server keeps
      // admission). See the `reconnecting` field.
      reconnect: this.reconnecting,
    })
    // RISKS.md/P1.19: adopt the short-lived SESSION access token the server minted for this
    // socket (the join-URL token is a long-lived join ticket). Keep the refresh token in
    // memory and (re)start silent refresh so the session token never expires mid-meeting.
    // Runs on every (re)join — the reconnect path re-invokes this.
    if (response.token) {
      this.setToken(response.token)
    }
    if (response.refreshToken) {
      this.refreshToken = response.refreshToken
    }
    this.joined = true
    this.scheduleRefresh()
    return response
  }

  // RISKS.md/P1.19: schedule a silent refresh shortly before the access token expires, so the
  // live socket always holds a valid token (this is what makes shortening the access TTL safe).
  private scheduleRefresh() {
    this.clearRefreshTimer()
    if (!this.token || !this.refreshToken) {
      return
    }
    let exp: number | undefined
    try {
      exp = jwtDecode<{ exp?: number }>(this.token).exp
    } catch {
      return
    }
    if (!exp) {
      return
    }
    // Refresh before expiry with a lead that ADAPTS to the token's remaining lifetime: cap it at
    // ~2min (covers background-tab timer throttling on a long TTL), but use at most a quarter of
    // the remaining time so the lead can never meet/exceed the TTL and spin into a tight refresh
    // loop (a fixed 2min lead under a <=2min TTL fires every few seconds). Floor at 5s for the
    // reconnect case where the token is already near expiry. The server grace covers residual lag.
    const msRemaining = exp * 1000 - Date.now()
    const lead = Math.min(120_000, msRemaining / 4)
    this.refreshTimer = setTimeout(() => this.refresh(), Math.max(5_000, msRemaining - lead))
  }

  // RISKS.md/P1.20: register a handler for "this session can no longer be refreshed". Every
  // terminal branch of refresh() fires it, so the UI can show a real notice instead of the user
  // sitting in a meeting whose token quietly stopped renewing (and then 401ing every REST call).
  public onSessionDead(handler: (reason: SessionDeadReason) => void) {
    this.sessionDeadHandlers.push(handler)
  }

  // RISKS.md/P1.20 + P1.22: terminal — announcing it is the point, since returning silently is what
  // made this failure invisible. A listener answers by disconnecting for good, so anything a retry
  // could still fix must not come here. Public for the re-join, its only caller outside this class.
  public sessionDead(reason: SessionDeadReason) {
    console.warn('session dead:', reason)
    this.clearRefreshTimer()
    this.sessionDeadHandlers.forEach(h => h(reason))
  }

  /**
   * RISKS.md/P1.20: refresh the access token NOW, bypassing the schedule — used by REST callers
   * that got a 401 so a write can be retried on a fresh token instead of being silently lost.
   * Resolves true when a new token was adopted. Joins an in-flight refresh rather than starting
   * a competing one (two rotations would trip reuse-detection).
   */
  public refreshNow(): Promise<boolean> {
    this.clearRefreshTimer()
    return this.refresh()
  }

  private refresh(): Promise<boolean> {
    if (this.refreshPromise) {
      return this.refreshPromise
    }
    this.refreshPromise = this.doRefresh().finally(() => { this.refreshPromise = null })
    return this.refreshPromise
  }

  private async doRefresh(): Promise<boolean> {
    if (!this.token || !this.refreshToken) {
      // Nothing to refresh from: we were never joined, or a previous attempt already gave up.
      this.sessionDead('invalid-token')
      return false
    }
    let sid: string | undefined
    try {
      sid = jwtDecode<TranslationConferenceTokenData>(this.token).sid
    } catch {
      this.sessionDead('invalid-token')
      return false
    }
    if (!sid) {
      this.sessionDead('invalid-token')
      return false
    }

    try {
      const res = await fetch(`${API_URL}/conference/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sid, refreshToken: this.refreshToken }),
      })
      if (res.status === 401) {
        // RISKS.md/P1.22: a ban and a plain revocation both 401 here — the kick DELETES the
        // session, so `sid` no longer maps to a user. Answering 'revoked' shows a banned user
        // "session expired", and its handler disconnects, killing the re-join that CAN tell the
        // two apart. So speak only on a live socket, where a kick already pushed `removed` down it.
        if (this.ws && this.ws.readyState === this.ws.OPEN) {
          this.sessionDead('revoked')
        } else {
          this.clearRefreshTimer()
        }
        return false
      }
      if (res.status >= 500) {
        // Server-side hiccup — treat like a network failure and retry (see catch below).
        throw new Error(`refresh failed with ${res.status}`)
      }
      const data = await res.json()
      if (!res.ok || !data.success || !data.token) {
        this.sessionDead('rejected')
        return false
      }
      this.refreshToken = data.refreshToken
      // Hand the new access token to the live socket BEFORE adopting it locally — the server
      // validates it against the still-current (old) token's sid/user/conference.
      // RISKS.md/P1.20: only while the socket is actually open. send() otherwise queues until it
      // reopens, which would stall a caller awaiting refreshNow() (a preferences save retrying
      // after a 401) for the whole reconnect backoff. Nothing is lost by skipping it: the
      // reconnect re-joins, and conference:join mints a fresh access token anyway.
      if (this.ws && this.ws.readyState === this.ws.OPEN) {
        await this.send<TokenRefreshRequest, TokenRefreshResponse>({ action: 'token:refresh', token: data.token })
      }
      this.setToken(data.token)
      this.scheduleRefresh()
      return true
    } catch (e) {
      // Transient (network / 5xx) failure — retry well before the (longer) access TTL elapses.
      console.error('token refresh error', e)
      this.refreshTimer = setTimeout(() => this.refresh(), 30_000)
      return false
    }
  }

  public async admitUser(room: string, uuid: string) {
    return this.send<ConferenceAdmitUserRequest, ConferenceAdmitUserResponse>({
      action: 'conference:admit-user',
      room,
      uuid,
    })
  }

  // RISKS.md/P1.19: moderator "remove user" (kick) — server revokes the target's sessions.
  public async removeUser(room: string, uuid: string) {
    return this.send<ConferenceRemoveUserRequest, ConferenceRemoveUserResponse>({
      action: 'conference:remove-user',
      room,
      uuid,
    })
  }

  // RISKS.md/P1.19: deliberately end the current session (real logout) — distinct from the
  // synthetic `leave` on socket close, which must not revoke. Server revokes this sid.
  public async endSession() {
    return this.send<SessionEndRequest, SessionEndResponse>({
      action: 'session:end',
    })
  }

  // Moderator only. Revokes nothing, unlike endSession(), which revokes our own session. No room
  // argument: the server takes it from our token (RISKS.md/P1.19).
  public async endConference() {
    return this.send<ConferenceEndRequest, ConferenceEndResponse>({
      action: 'conference:end',
    })
  }

  public setToken(token: string) {
    this.token = token
  }

  // RISKS.md/P1.22: `onOpen` is the FIRST-join flow, `onReopen` the re-join — collapsing them back
  // runs the whole first join on every reconnect. Anything a re-join must not repeat goes in `onOpen`.
  public connect(onOpen: () => void, onReopen?: () => void) {
    // RISKS.md/P1.3: a new connect() lifts the terminal disconnect() state.
    this.closed = false
    this.ws = new WebSocket(this.url, this.protocols)

    this.ws.onerror = () => console.error('websocket error')
    this.ws.onclose = () => {
      // RISKS.md/P1.19: stop the refresh timer on any close; a reconnect re-joins and
      // reschedules, so a stale timer can't fire a refresh over a dead/reconnecting socket.
      this.clearRefreshTimer()
      // RISKS.md/P1.3: settle every in-flight request — its reply can never arrive on this
      // socket, so an awaiting caller would otherwise hang forever.
      for (const messageId of Object.keys(this.messageCallbacks)) {
        this.messageCallbacks[messageId].reject(new Error('websocket disconnected', { cause: DISCONNECTED_CAUSE }))
        delete this.messageCallbacks[messageId]
      }
      this.connectionStateHandlers.forEach(h => h(false))
      if (!this.disconnected) {
        // Transient drop: this same client auto-reconnects, so the re-join keeps admission
        // (a network blip must not bounce the user back to the waiting room).
        if (this.joined) {
          this.reconnecting = true
        }
        // RISKS.md/P1.3: exponential backoff + jitter, capped at 30s (was a fixed 500ms loop —
        // a reconnection storm against a struggling server). Never gives up: a meeting client
        // should keep trying, and the counter resets once a connection opens.
        const backoff = Math.min(30_000, 500 * 2 ** this.reconnectAttempts)
        this.reconnectAttempts++
        this.reconnectTimer = setTimeout(() => this.connect(onOpen, onReopen), backoff + Math.random() * 250)
      } else {
        this.disconnected = false
      }
    }

    this.ws.onopen = () => {
      // RISKS.md/P1.3: a live connection resets the reconnect backoff.
      this.reconnectAttempts = 0
      this.connectionStateHandlers.forEach(h => h(true))
      if (this.reconnecting && onReopen) {
        onReopen()
      } else {
        onOpen()
      }
    }

    this.ws.onmessage = (message) => {
      const data: ServerResponse<WsResponse|ErrorResponse> = JSON.parse(message.data)

      if (typeof data.messageId !== 'undefined' && typeof this.messageCallbacks[data.messageId] !== 'undefined') {
        // @ts-ignore
        if (typeof data.error === 'undefined') {
          this.messageCallbacks[data.messageId].resolve(data)
        } else {
          // @ts-ignore
          this.messageCallbacks[data.messageId].reject(new Error(data.error.message, { cause: data.error.code }))
        }
        delete this.messageCallbacks[data.messageId]
      } else if (this.callback) {
        this.callback(data)
      } else {
        console.log('WSCLIENT', this.instanceId, 'no message callback registered')
      }
    }
  }
}